import { Component, OnDestroy, OnInit } from '@angular/core';
import { ANGULAR_MODULES, OperationsComponent, TopBarComponent } from '../Shared/shared-imports';
import { WordReplacementService } from '../services/word-replacement.service';
import { GeneralService } from '../services/general.service';
import { Subscription, take } from 'rxjs';
import { DashboardService } from '../services/dashboard.service';
import { LoaderComponent } from '../Shared/loader/loader.component';
import { AccumulationChartAllModule, CategoryService, ChartAllModule, ChartAreaModel, ChartModule, ColumnSeriesService, LegendService, TooltipRenderService, TooltipService } from '@syncfusion/ej2-angular-charts';
import { DatePipe } from '@angular/common';
import { WordReplacePipe } from '../Shared/word-replace.pipe';

@Component({
  selector: 'app-dashboard',
  imports: [TopBarComponent, ANGULAR_MODULES, OperationsComponent, LoaderComponent, ChartModule, ChartAllModule, AccumulationChartAllModule, WordReplacePipe],
  providers: [CategoryService, ColumnSeriesService, TooltipService, LegendService, TooltipRenderService],
  standalone: true,
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss'
})
export class DashboardComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  constructor(private wordReplacementService: WordReplacementService,
    private generalService: GeneralService,
    private dashboardService: DashboardService,
    private datePipe: DatePipe
  ) { }

  public legendSettingsSpaceStatistics: Object;
  public legendSettings: Object;
  public legendSettingsWeekly: Object;
  public pointColorMapping?: string;
  isCollapsed = true;
  currentDateSalesData;
  noSalesData = true;
  public chartBoothRevenueData: Object[] = [];
  public chartOtherItemRevenueData: Object[] = [];
  public loaderIcon: Promise<boolean>;
  noBoothRevenueData = true;
  noOtherItemRevenueData = true;
  BoothRevenueData = [];
  otherItemRevenueData = [];
  public primaryXAxisRentRevenue?: Object;
  public primaryYAxisRentRevenue?: Object;
  public primaryXAxisOtherRentRevenue?: Object;
  public primaryYAxisOtherRentRevenue?: Object;
  public palette?: string[];
  public chartPalettes?: string[];
  public chartPalettesWeekly?: string[];
  public monthlyChartData = [];
  public hourlyChartData = [];
  weeklyChartData: any[] = [];
  public primaryXAxisMonth: any = {
    valueType: 'Category',
    labelRotation: 90,       // Rotate labels vertically
    labelIntersectAction: 'None', // Show all labels even if crowded
    interval: 1,             // Force display of every label
  };
  primaryYAxisMonth: any;
  public primaryXAxisWeek: any = {
    valueType: 'Category',
    labelRotation: 90,
    interval: 1,
    labelIntersectAction: 'None'
  };
  primaryYAxisWeek: any;
  primaryXAxisHour: any = {
    valueType: 'Category',
    labelRotation: 90,
    interval: 1,
    labelIntersectAction: 'None'
  };
  primaryYAxisHour: any;
  public chartArea: ChartAreaModel;
  public chartAreaMonthly: ChartAreaModel;
  public chartAreaWeekly: ChartAreaModel;
  public chartAreaHourly: ChartAreaModel
  public marker: any;

  currentDate;
  marketCurrentDate: Date = new Date();
  public tooltip = {
    enable: true,
    format: '${point.x} : $${point.y}', // show $ symbol
    useGroupingSeparator: true
  };
  public tooltip1 = {
    enable: true,
    useGroupingSeparator: true
  };

  tooltipForRental: Object = {
    enable: true
  };

  onTooltipRender(args: any) {
    if (args.series.name === "Total Price ($)") {
      // Add $ for price
      args.text = `${args.point.x} : $${args.point.y}`;
    } else {
      // No $ for count
      args.text = `${args.point.x} : ${args.point.y}`;
    }
  }

  handleToggle(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  public secondaryAxes: any[] = [
    {
      name: 'countAxis',
      opposedPosition: true,
      title: 'Total Count',
      minimum: 0,
      maximum: 100,
      interval: 20
    }
  ];

  public secondaryAxesWeekly: any[] = [
    {
      name: 'countAxis',
      opposedPosition: true,
      title: 'Total Count',
      minimum: 0,
      maximum: 100,
      interval: 20
    }
  ];

  public secondaryAxesHourly: any[] = [
    {
      name: 'countAxis',
      opposedPosition: true,
      title: 'Total Count',
      minimum: 0,
      maximum: 100,
      interval: 20
    }
  ];

  ngOnInit(): void {
    this.palette = ['red', 'violet', 'blue', 'cyan', 'green', 'pink'];
    this.loadDashboardSalesData();
    this.setMarketTerms();
    this.getBoothRentalRevenue();
    this.getOtherItemRentalRevenue();
  }

  setMarketTerms() {
    this.subscriptions.push(this.generalService.getTermsUsedInMarket().subscribe(res => {
      this.wordReplacementService.setTermMap({
        booth: res.BoothTerm,
        vendor: res.VendorTerm,
        market: res.MarketTerm
      });
    }))
  }

  loadDashboardSalesData(): void {
    this.generalService.GetMarketCurrentTime().pipe(take(1)).subscribe({
      next: (marketTime: string) => {
        if (marketTime) {
          const marketDate = marketTime.split('T')[0];
          this.marketCurrentDate = marketDate ? this.toLocalDate(marketDate) : this.getDateOnly();
        } else {
          this.marketCurrentDate = this.getDateOnly();
        }

        this.currentDate = this.datePipe.transform(this.marketCurrentDate, 'MM/dd/yyyy');
        this.loadDashboardSalesCharts();
      },
      error: (err) => {
        console.error('Error fetching market time. Using local date.', err);
        this.marketCurrentDate = this.getDateOnly();
        this.currentDate = this.datePipe.transform(this.marketCurrentDate, 'MM/dd/yyyy');
        this.loadDashboardSalesCharts();
      }
    });
  }

  loadDashboardSalesCharts(): void {
    this.getSalesDataForCurrentDate();
    this.loadMonthlyChart();
    this.loadWeeklyChart();
    this.loadHourlyChart();
  }

  toLocalDate(dateString: string): Date {
    const [y, M, d] = dateString.split('-').map(Number);
    return new Date(y, M - 1, d);
  }

  private getDateOnly(): Date {
    const today = new Date();

    return new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
  }

  getBoothRentalRevenue() {
    this.loaderIcon = Promise.resolve(true);
    this.chartBoothRevenueData = [];

    this.dashboardService.getBoothRentalRevenue().subscribe({
      next: (res: any[]) => {
        this.BoothRevenueData = res;

        if (this.BoothRevenueData.length > 0) {
          this.noBoothRevenueData = false;
          const sortedData = [...this.BoothRevenueData]
            .sort((a, b) => a.YearMonth.localeCompare(b.YearMonth));

          const uniqueMonths = Array.from(
            new Map(
              sortedData.map(item => [
                item.MonthYear.substring(0, 3),
                item
              ])
            ).values()
          );

          uniqueMonths.sort((a, b) =>
            a.YearMonth.localeCompare(b.YearMonth)
          );

          const last12Months = uniqueMonths.slice(-12);

          this.chartBoothRevenueData = last12Months.map((el, i) => ({
            x: el.MonthYear.substring(0, 3),
            y: el.Rent,
            color: this.getColorForChart(i)
          }));

          this.primaryXAxisRentRevenue = {
            valueType: 'Category',
            majorGridLines: { width: 0 },
            title: 'Month'
          };

          this.primaryYAxisRentRevenue = {
            labelFormat: '${value}',
            lineStyle: { width: 0 },
            majorTickLines: { width: 0 },
            minorTickLines: { width: 0 },
            title: 'Booth Rental Revenue'
          };

          this.chartArea = { border: { width: 0 } };
          this.pointColorMapping = "color";
        } else {
          this.noBoothRevenueData = true;
        }

        this.loaderIcon = Promise.resolve(false);
      },

      error: (err) => {
        console.error('Error fetching booth rental revenue', err);
        this.loaderIcon = Promise.resolve(false);
      }
    });
  }

  getOtherItemRentalRevenue() {
    this.loaderIcon = Promise.resolve(true);
    this.chartOtherItemRevenueData = [];

    this.subscriptions.push(
      this.dashboardService.getOtherRentalRevenue().subscribe({
        next: (res: any[]) => {
          this.otherItemRevenueData = res;

          if (this.otherItemRevenueData.length > 0) {
            this.noOtherItemRevenueData = false;

            const sortedData = [...this.otherItemRevenueData]
              .sort((a, b) => a.YearMonth.localeCompare(b.YearMonth));

            const uniqueMonths = Array.from(
              new Map(
                sortedData.map(item => [
                  item.MonthYear.substring(0, 3),
                  item
                ])
              ).values()
            );

            uniqueMonths.sort((a, b) =>
              a.YearMonth.localeCompare(b.YearMonth)
            );

            const last12Months = uniqueMonths.slice(-12);

            this.chartOtherItemRevenueData = last12Months.map((el, i) => ({
              x: el.MonthYear.substring(0, 3),
              y: el.OtherChg,
              color: this.getColorForChart(i)
            }));

            this.primaryXAxisOtherRentRevenue = {
              valueType: 'Category',
              majorGridLines: { width: 0 },
              title: 'Month'
            };

            this.primaryYAxisOtherRentRevenue = {
              labelFormat: '${value}',
              lineStyle: { width: 0 },
              majorTickLines: { width: 0 },
              minorTickLines: { width: 0 },
              title: 'Other Items Rental Revenue'
            };

            this.chartArea = {
              border: { width: 0 }
            };

            this.pointColorMapping = "color";

            this.tooltip = {
              enable: true,
              format: '${point.x} : $${point.y}',
              useGroupingSeparator: true
            };

          } else {
            this.noOtherItemRevenueData = true;
          }

          this.loaderIcon = Promise.resolve(false);
        },
        error: () => {
          this.loaderIcon = Promise.resolve(false);
        }
      })
    );
  }

  loadWeeklyChart(): void {
    const today = new Date(this.marketCurrentDate);
    const fromDate = new Date(today);
    fromDate.setDate(today.getDate() - 6);

    const fromDateStr = this.datePipe.transform(fromDate, 'yyyy-MM-dd');
    const toDateStr = this.datePipe.transform(today, 'yyyy-MM-dd');

    const obj = {
      FromDate: fromDateStr,
      ToDate: toDateStr
    };

    this.dashboardService.getSalesDataForDateRange(obj).subscribe({
      next: (res: any[]) => {
        this.loaderIcon = Promise.resolve(false);

        if (res && res.length > 0) {
          const tempData = res.map(data => ({
            day: this.formatDay(data.ReceiptDate),
            price: data.TotalSales,
            count: data.TotalSalesCount
          }));

          this.weeklyChartData = [...tempData];
          // Define chart properties once
          this.primaryXAxisWeek = {
            valueType: 'Category',
            title: 'Day',
            majorGridLines: { width: 0 }
          };
          this.primaryYAxisWeek = {
            title: 'Revenue',
            majorTickLines: { width: 0 },
            lineStyle: { width: 0 }
          };

          this.chartAreaWeekly = { border: { width: 0 } };
          this.marker = { dataLabel: { visible: false } };
          this.chartPalettesWeekly = [
            '#F94144', // Red-orange
            '#F3722C', // Orange
          ];
        } else {
          this.weeklyChartData = []; // Handle empty case
        }
      },
      error: (err) => {
        this.loaderIcon = Promise.resolve(false);
        console.error('Error fetching sales data', err);
      }
    });
  }

  private formatDay(dateStr: string): string {
    const datePart = dateStr.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);

    const date = new Date(year, month - 1, day);

    return date.toLocaleDateString('en-US', { weekday: 'short' }); // e.g., Mon, Tue, ...
  }

  loadMonthlyChart() {
    this.loaderIcon = Promise.resolve(true);
    this.dashboardService.getSalesDataForDateRangeByMonth(true, false).subscribe({
      next: (res: any[]) => {
        this.loaderIcon = Promise.resolve(false);

        if (res && res.length > 0) {
          const tempData = res.map(data => ({
            month: data.MonthYear.substring(0, 3), // <-- use 'month', not 'data'
            price: data.TotalSales,
            count: data.TotalSalesCount
          }));

          this.monthlyChartData = [...tempData];

          // Define chart properties once
          this.primaryXAxisMonth = {
            valueType: 'Category',
            title: 'Month',
            majorGridLines: { width: 0 }
          };

          this.primaryYAxisMonth = {
            title: 'Revenue',
            majorTickLines: { width: 0 },
            lineStyle: { width: 0 }
          };

          this.chartAreaMonthly = { border: { width: 0 } };
          this.marker = { dataLabel: { visible: false } };
          this.chartPalettes = [
            '#FF6384', '#36A2EB'];
        } else {
          this.monthlyChartData = []; // Handle empty case
        }
      },
      error: (err) => {
        this.loaderIcon = Promise.resolve(false);
        console.error('Error fetching sales data', err);
      }
    });
  }

  loadHourlyChart() {
    this.loaderIcon = Promise.resolve(true);
    this.dashboardService.getSalesDataForDateRangeByHour().subscribe({
      next: (res: any[]) => {
        this.loaderIcon = Promise.resolve(false);

        if (res && res.length > 0) {
          const tempData = res.map(data => ({
            hour: data.HourLabel, // <-- use 'month', not 'data'
            price: data.TotalSales,
            count: data.TotalSalesCount
          }));

          this.hourlyChartData = [...tempData];

          // Define chart properties once
          this.primaryXAxisHour = {
            valueType: 'Category',
            title: 'Hour',
            majorGridLines: { width: 0 }
          };

          this.primaryYAxisHour = {
            title: 'Revenue',
            majorTickLines: { width: 0 },
            lineStyle: { width: 0 }
          };

          this.chartAreaHourly = { border: { width: 0 } };
          this.marker = { dataLabel: { visible: false } };
          this.chartPalettes = [
            '#FF6384', '#36A2EB'];
        } else {
          this.hourlyChartData = []; // Handle empty case
        }
      },
      error: (err) => {
        this.loaderIcon = Promise.resolve(false);
        console.error('Error fetching sales data', err);
      }
    });
  }

  getSalesDataForCurrentDate(): void {
    this.loaderIcon = Promise.resolve(true);
    this.currentDateSalesData = [];
    this.noSalesData = false;
    const todayStr = this.datePipe.transform(this.marketCurrentDate, 'yyyy-MM-dd');
    const obj = {
      FromDate: todayStr, // format as yyyy-MM-dd
      ToDate: todayStr
    };

    this.dashboardService.getSalesDataForDateRange(obj).subscribe({
      next: (res: any[]) => {
        this.loaderIcon = Promise.resolve(false);

        if (res && res.length > 0) {
          const salesData = res[0];
          this.currentDateSalesData = [
            { data: salesData.RetailSales, label: "Retail Sales" },
            { data: salesData.WholesaleSale, label: "Wholesale Sale" },
            { data: salesData.LayawaySale, label: "Layaway Sale" },
            { data: salesData.ReturnSales, label: "Return Sales" },
            { data: salesData.OnlineSale, label: "Online Sale" },
            { data: (salesData.VoidSales * -1), label: "Void Sale" }
          ];
        } else {
          this.currentDateSalesData = [];
          this.noSalesData = true;
        }
      },
      error: (err) => {
        console.error("Error fetching sales data", err);
        this.loaderIcon = Promise.resolve(false);
      }
    });
  }

  getColorForOtherItemRevenue(index: number): string {
    const colors = ['#57FF33', '#33C5FF', '#FFD733', 'cyan', '#FF33C5', '#33FF57', '#D733FF', '#FF5733', '#33FFC5', '#C533FF', '#FF5733', '#33FFC5'];
    const colorIndex = index % colors.length;
    return colors[colorIndex];
  }

  getColorForChart(index: number): string {
    const colors = [
      '#C7CF3D', // Yellow-green
      '#67C1CC', // Light teal
      '#2E6577', // Dark teal
      '#EA676E', // Coral red
      '#F6B436', // Golden yellow
      '#9A78B0', // Lavender purple
      '#3DB6AE', // Teal green
      '#66AA00', // lime green
      '#B82E2E', // dark red
      '#316395', // steel blue
      '#994499', // violet
      '#22AA99'  // turquoise
    ];

    const colorIndex = index % colors.length;
    return colors[colorIndex];
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }
}
